import React from 'react'
import type { CommonDeps } from '../../../app-core/lib/base/CommonDeps'
import type { RenderCachedImage } from '../../../app-core/lib/image/WithCachedImage'
import { getReportHistoryMetaData } from '../../../app-core/lib/report/ReportHistoryMetadata'
import { ViewReport } from '../../../app-core/lib/report/viewReport/ViewReport'
import { SVG_Cross } from '../../../app-core/lib/svg/SVG_Cross'
import { ModalBottomSheet } from '../../../app-core/lib/ui/ModalBottomSheet'
import type { HistoricalReport } from '../../../shared/domain/viewableReports/types.no-deps'
import { Translations } from '../../../shared/i18n/Translations'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { TranslateButton } from '../ui/TranslateButton'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'

interface TopBarContentProps {
    onClose: () => void
    report: HistoricalReport | null
}

function TopBarContent(props: TopBarContentProps) {
    return (
        <div className="Rpts__AncestorReportModalBotttomSheet__TopBarContent">
            <button aria-label="close" onClick={props.onClose}>
                <SVG_Cross color="var(--grey-600)" />
            </button>
            {props.report && (
                <div className="Rpts__AncestorReportModalBotttomSheet__TopBarContent-title">
                    <zaptic-ellipsis>
                        <span className="Rpts__AncestorReportModalBotttomSheet__TopBarContent-titleNumber">
                            #{props.report.reportViewData.reportId} -{' '}
                        </span>
                        {props.report.reportViewData.title}
                    </zaptic-ellipsis>
                </div>
            )}
        </div>
    )
}

interface BottomSheetContentProps {
    deps: CommonDeps
    onOpenFile: (url: string, name: string, mimeType: string) => Promise<void>
    onReportPostClick: (postId: UUID) => void
    renderCachedImage: RenderCachedImage
    report: HistoricalReport
    routeToReturnHere: () => void
}

function BottomSheetContent(props: BottomSheetContentProps) {
    const { reportName, reportSubject, description, createdBy, dateAndTime } = getReportHistoryMetaData(
        props.deps,
        props.renderCachedImage,
        props.report
    )

    return (
        <div className="Rpts__AncestorReportModalBotttomSheet__BottomSheetContent">
            <div className="AppCore__report__HistoryMetaData Rpts__AncestorReportModalBotttomSheet__BottomSheetContent-metadata">
                {reportName}
                {reportSubject}
                {createdBy}
                {description}
                {dateAndTime}
            </div>

            {props.report.reportPostId && (
                <div className="Rpts__AncestorReportModalBotttomSheet__BottomSheetContent-goToReportSection">
                    <zaptic-button
                        type="link"
                        size="large"
                        onClick={() => props.onReportPostClick(props.report.reportPostId)}
                    >
                        {Translations.t('appcore:ReportHistory.goToReportTimeline')}
                    </zaptic-button>
                </div>
            )}

            <ViewReport
                deps={props.deps}
                onOpenFile={props.onOpenFile}
                onReportPostClick={props.onReportPostClick}
                photoUrls={props.report.reportViewData.photoUrls}
                renderCachedImage={props.renderCachedImage}
                report={props.report.reportViewData}
                reportDetails={props.report.reportViewData.reportDetails}
                unitTypes={props.report.reportViewData.unitTypesInAnswers}
                units={props.report.reportViewData.unitsInAnswers}
                allowImageZoom={true}
            ></ViewReport>
        </div>
    )
}

interface Props {
    deps: CommonDeps
    featureFlags: Pick<CamelCaseFlags, 'reportsTranslation'>
    fetchTranslation: () => Promise<HistoricalReport>
    onClose: () => void
    onOpenFile: (url: string, name: string, mimeType: string) => Promise<void>
    onReportPostClick: (postId: UUID) => void
    onTranslate: (report?: HistoricalReport) => void
    renderCachedImage: RenderCachedImage
    report: HistoricalReport | null
    routeToReturnHere: () => void
}

export function OriginatingReportModalBottomSheet(props: Props) {
    const {
        deps,
        featureFlags,
        fetchTranslation,
        onClose,
        onOpenFile,
        onReportPostClick,
        onTranslate,
        renderCachedImage,
        report,
        routeToReturnHere,
    } = props

    return (
        <ModalBottomSheet open={report !== null} onClose={onClose} size={'large'}>
            <div className="Rpts__AncestorReportModalBotttomSheet">
                <TopBarContent onClose={onClose} report={report} />

                {props.report && (
                    <>
                        {featureFlags.reportsTranslation && (
                            <div className="Rpts__AncestorReportModalBotttomSheet_TranslateButton">
                                <TranslateButton
                                    deps={deps}
                                    id={report.report.id}
                                    language={report.report.language}
                                    onTranslate={onTranslate}
                                    fetchTranslation={fetchTranslation}
                                />
                            </div>
                        )}
                        <BottomSheetContent
                            deps={deps}
                            onOpenFile={onOpenFile}
                            onReportPostClick={onReportPostClick}
                            renderCachedImage={renderCachedImage}
                            report={report}
                            routeToReturnHere={routeToReturnHere}
                        />
                    </>
                )}
            </div>
        </ModalBottomSheet>
    )
}
